// WDCE-199 step 9g-1 (D56): the EFTPOS payment screen's timing and requests, around paymentScreen.js's next(),
// pollRequest() and pollDelay() and eftposApi.js. No React or DOM: everything it uses is passed in, so tests control
// time and every answer.
//
//   const session = createPaymentSession({
//     api: createEftposApi({ ... }),
//     setTimeout: window.setTimeout.bind(window),
//     clearTimeout: window.clearTimeout.bind(window),
//     onChange: (state) => ... // each time the state object changes
//     onKeyBusyChange: (busy) => ... // optional: each time a key request goes out or is done with (step 9g-3a)
//   })
//   session.lookup()                                       // once, on page load
//   session.pay({ eftposTerminalId, processAndPrint })     // after the checkout PATCH succeeds
//   session.pressKey(key, data)                            // a PIN pad key
//   session.wake()                                         // the tab is visible again
//   session.stop()                                         // the screen is going away
//   session.state()
//   session.keyBusy()                                      // a key request is out
//
// - Requests go one at a time on two lines: the payment line (start, status and lookup) and the key line (sendKey),
//   so a key can go while a status read is outstanding, but never two of either.
// - After every answer on either line, the next status read or lookup is timed from pollRequest and pollDelay, even
//   when next() kept the same state object (it does that for an answer it has moved past). Nothing is timed while a
//   payment line request is outstanding, and there's one timer at most.
// - Pay moves past whatever the payment and key lines had outstanding (the page load lookup, say): their answers are
//   ignored, as is every answer after stop().
// - keyBusy (step 9g-3a): the key buttons stay disabled while a key request is out, not only while state.keySending is
//   set, which a new prompt clears. onKeyBusyChange hears each change, as the state object may not change with it;
//   never after stop().
// - It never throws for the api: eftposApi.js never rejects, but a throw or rejection counts as a network error, and
//   is logged to the browser console.

import { next, pollRequest, pollDelay, initialState } from './paymentScreen'

const NETWORK_ERROR = { networkError: true }

// eftposApi.js never throws or rejects for a failed request, so this is a bug in the browser code: log it, so it
// isn't passed off silently as a network error. The error only, never what was sent or what came back.
function failed(error) {
  console.error('EFTPOS request failed in the browser', error)
  return NETWORK_ERROR
}

// The answer, or a network error if the call throws or its promise rejects.
function ask(call) {
  try {
    return Promise.resolve(call()).catch(failed)
  } catch (error) {
    return Promise.resolve(failed(error))
  }
}

export function createPaymentSession({ api, setTimeout, clearTimeout, onChange, onKeyBusyChange }) {
  let current = initialState()
  let stopped = false
  let lookupSent = false
  let timer = null
  // The outstanding request on each line, or null. An answer is applied only if its request is still the one here.
  let paymentRequest = null
  let keyRequest = null

  const dispatch = (event) => {
    const updated = next(current, event)
    if (updated === current) return

    current = updated
    onChange(current)
  }

  // The key line's outstanding request, telling onKeyBusyChange when that makes it busy or free. Nothing calls this
  // after stop(): pay, pressKey and the key answer all return first.
  const setKeyRequest = (request) => {
    const changed = (keyRequest !== null) !== (request !== null)
    keyRequest = request
    if (changed && onKeyBusyChange) onKeyBusyChange(request !== null)
  }

  const cancelTimer = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
  }

  const schedule = () => {
    if (stopped || paymentRequest !== null) return

    cancelTimer()
    const delay = pollDelay(current)
    if (delay !== null) timer = setTimeout(poll, delay)
  }

  // Sends a request on the payment line; its answer becomes this event.
  function send(call, type) {
    const request = {}
    paymentRequest = request
    ask(call).then((result) => {
      if (stopped || paymentRequest !== request) return

      paymentRequest = null
      dispatch({ type, result })
      schedule()
    })
  }

  function poll() {
    timer = null
    const request = pollRequest(current)
    if (stopped || paymentRequest !== null || request === null) return

    if (request.kind === 'status') send(() => api.status(request.paymentId), 'polled')
    else send(() => api.lookup(), 'lookedUp')
  }

  return {
    state: () => current,

    keyBusy: () => keyRequest !== null,

    lookup() {
      if (stopped || lookupSent || paymentRequest !== null) return

      lookupSent = true
      send(() => api.lookup(), 'lookedUp')
    },

    pay({ eftposTerminalId, processAndPrint }) {
      if (stopped) return
      const before = current
      dispatch({ type: 'pay' })
      if (current === before) return

      cancelTimer()
      setKeyRequest(null)
      send(() => api.start({ eftposTerminalId, processAndPrint }), 'started')
    },

    pressKey(key, data) {
      if (stopped || keyRequest !== null) return
      const before = current
      dispatch({ type: 'keyPressed', key })
      if (current === before) return

      const request = {}
      setKeyRequest(request)
      const paymentId = current.payment.id
      ask(() => api.sendKey(paymentId, key, data)).then((result) => {
        if (stopped || keyRequest !== request) return

        setKeyRequest(null)
        dispatch({ type: 'keyAnswered', result })
        schedule()
      })
    },

    wake() {
      if (stopped || timer === null || paymentRequest !== null) return

      cancelTimer()
      poll()
    },

    stop() {
      stopped = true
      cancelTimer()
    }
  }
}
